import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { omit } from 'lodash';
import { BackChevron } from '../BackChevron';

const BackNavigationContext = createContext(null);

let nextStepId = 0;

// Un solo chevron por pantalla, con el back más específico disponible: cada
// paso interno (editar Telcel, detalle de tarjeta, detalle de cuenta PayPal...)
// se anota aquí mientras está montado, y el Header ejecuta el último que
// quede anotado en vez de que cada pantalla pinte su propio chevron al lado.
// Como esos pasos son mutuamente excluyentes (un gateway a la vez, y una sola
// vista dentro de él a la vez), un registro simple por id alcanza: no hace
// falta modelar una pila de navegación completa.
export const BackNavigationProvider = ({ children }) => {
  const [steps, setSteps] = useState({});

  const registerStep = useCallback(onBack => {
    const id = nextStepId++;
    setSteps(prev => ({ ...prev, [id]: onBack }));

    return () => setSteps(prev => omit(prev, id));
  }, []);

  const stepIds = Object.keys(steps);
  const activeOnBack = stepIds.length
    ? steps[stepIds[stepIds.length - 1]]
    : null;

  const value = useMemo(
    () => ({ registerStep, activeOnBack }),
    [registerStep, activeOnBack],
  );

  return (
    <BackNavigationContext.Provider value={value}>
      {children}
    </BackNavigationContext.Provider>
  );
};

// Anota `onBack` como el back vigente mientras el componente que llama al
// hook siga montado y `enabled` sea true. Pensado para pantallas que hoy
// pintan su propio chevron: en vez de eso, delegan en el del Header.
//
// El efecto depende de `registerStep`, no del objeto de contexto completo:
// ese objeto cambia de referencia en cada registro/baja (arrastra
// `activeOnBack`), y `registerStep` es estable (useCallback con deps []).
// Si el efecto dependiera del contexto entero, cada registro dispararía un
// nuevo valor de contexto, que re-dispararía este mismo efecto con un id
// nuevo, en un loop infinito.
export const useBackNavigationStep = (onBack, { enabled = true } = {}) => {
  const registerStep = useContext(BackNavigationContext)?.registerStep;
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  useEffect(() => {
    if (!registerStep || !enabled) return undefined;

    return registerStep(() => onBackRef.current());
  }, [registerStep, enabled]);
};

// Consumido por el Header (angosto, fusionado con su propio chevron — ver
// `respectActiveStep`) y por `ActiveStepBackChevron` (ancho): el back del
// paso activo, o null si ningún paso interno se anotó.
export const useActiveBackNavigationStep = () =>
  useContext(BackNavigationContext)?.activeOnBack ?? null;

// Chevron propio del panel de detalle en el layout ancho (desktop). Ahí la
// lista y el detalle conviven como dos columnas separadas, así que el
// chevron del Header (arriba de todo, sale de la app) y este (vuelve del
// paso interno activo: editar Telcel, detalle de tarjeta/cuenta...) son dos
// elementos en dos contenedores distintos y no se confunden — a diferencia
// del angosto, donde ambos quedarían apilados en la misma columna y por eso
// ahí el Header absorbe el paso activo en vez de mostrar los dos.
export const ActiveStepBackChevron = () => {
  const activeOnBack = useActiveBackNavigationStep();

  if (!activeOnBack) return null;

  return <BackChevron onClick={activeOnBack} />;
};
